<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onTransferSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by id"
          name="search"
        />
        <app-date-picker @change="onChangeFilter" v-model="filter_start" placeholder="Date from" class="datepicker--filter" />
        <app-date-picker @change="onChangeFilter" v-model="filter_end" placeholder="Date to" class="datepicker--filter" />
        <app-select
          @input="onChangeFilter"
          v-model="responsible_filter"
          :options="responsible_center_options"
          placeholder="Responsible center"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-if="responsible_filter"
          v-model="department_filter"
          :reduce="item => item.code"
          :options="department_options"
          placeholder="Department"
          class="select--200"
        />
        <app-filters
          v-if="is_general_manager || is_product_storage_supervisor"
          v-model="filter_checked"
          :filters="filters"
          name="filterTransfers"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__id>
          <div class="table-default__left">ID</div>
        </template>
        <template #h__employee>
          <div class="table-default__left">Taken by</div>
        </template>
        <template #id="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'product-storage-transfer-detail', params: {id: row.id }}" class="table-default__link-border">
              Transfer - {{ row.id }}
            </router-link>
          </div>
        </template>
        <template #date="{ row }">
          {{ dateFormat(row.date) }}
        </template>
        <template #products_amount="{ row }">
          {{ row.products_amount ? row.products_amount.toLocaleString() : 0 }} TSh
        </template>
        <template #transport_amount="{ row }">
          <span
            v-tooltip="{content: tooltipContent(row), placement: 'top-start', offset: 10, html: true}"
          >
            {{ row.transport_amount.toLocaleString() }} TSh
          </span>
        </template>
        <template #responsible_center="{ row }">
          {{ row.responsible_center ? row.responsible_center.name : '-' }}
        </template>
        <template #responsible_center_department="{ row }">
          {{ row.responsible_center_department ? row.responsible_center_department.name : '-' }}
        </template>
        <template #employee="{ row }">
          <div v-if="row.employee" class="table-default__left">
            <router-link
              v-if="is_general_manager"
              :to="{name: 'employees-profile', params: {id: row.employee.id }}"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
            <div v-else class="table-default__left">{{ row.employee.full_name }}</div>
          </div>
          <div v-if="row.mountain_crew" class="table-default__left">
            <router-link
              v-if="is_general_manager"
              :to="{name: 'climbing-mountain-crew-detail', params: {id: row.mountain_crew.id }}"
              class="table-default__link-border"
            >
              {{ row.mountain_crew.full_name }}
            </router-link>
            <div v-else class="table-default__left">{{ row.mountain_crew.full_name }}</div>
          </div>
          <template v-if="!row.employee && !row.mountain_crew">-</template>
        </template>
        <template #checked="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.checked ? '#77b017' : '#8a8a8a' }"
          >
            {{ row.checked ? 'Yes' : 'No' }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getProductStorageResponsibleCenter, getProductStorageTransfers } from '@/http'
import debounce from 'lodash/debounce'
import { format, parse } from 'date-fns'

export default {
  name: 'ProductStorageTransfersCompleted',
  data() {
    return {
      is_loaded: false,
      table: {
        items: [],
        columns: ['id', 'date', 'products_amount', 'transport_amount', 'responsible_center', 'responsible_center_department', 'employee', 'checked'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'products_amount': 'Value',
            'transport_amount': 'Transport',
            'responsible_center': 'Responsible center',
            'responsible_center_department': 'Department',
            'employee': 'Taken by',
            'checked': 'Checked',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      filter_start: null,
      filter_end: null,
      filter_checked: '',
      search: '',
      responsible_filter: '',
      department_filter: '',
      responsible_center_options: [],
      department_options: [],
      filters: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Checked' },
        { value: 'false', label: 'Not checked' },
      ],
      table_preloader: false
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('product_storage_supervisor')
    },
  },
  created() {
    this.fetchItems()
    getProductStorageResponsibleCenter().then(response => { this.responsible_center_options = response.data })
  },
  methods: {
    tooltipContent(data) {
      if (!data.transport) return ''
      return `${data.transport.name} (${data.transport_qty})`
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.search = ''
      if (this.responsible_filter) {
        this.department_options = this.responsible_filter.departments
      } else {
        this.department_filter = ''
      }
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    fetchItems() {
      getProductStorageTransfers({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        climbing: false,
        status: 'completed',
        responsible_center: this.search ? '' : this.responsible_filter && this.responsible_filter.code,
        department: this.search ? '' : this.department_filter,
        date_range_after: this.search ? '' : this.filter_start,
        date_range_before: this.search ? '' : this.filter_end,
        checked: this.search ? '' : this.filter_checked
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onTransferSearch() {
      this.searchTransfer(this)
    },
    searchTransfer: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
